import { c as _c } from "react/compiler-runtime";
import { IcoArrowRightSmall16, IcoPlusSmall16 } from '@onefootprint/icons';
import { Button, Card, LinkButton } from '@onefootprint/ui';
import { parseAsString, useQueryState } from 'nuqs';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TaskBlockProvider } from '../components/agent-blocks/task-block-context';
import ArtifactSuggestions from '../components/suggestion/artifact-suggestions';
import OutputsSuggestions from '../components/suggestion/outputs-suggestions';
import ValidationAlerts from '../components/validation-alerts';
import useBlankReportName from '../hooks/use-blank-report-name';
import { useNodeSave } from '../hooks/use-node-save';
import { useBuilder } from '../store/provider';
import { type Artifact, isTaskNode, OUTPUTS_NODE_ID, type Task } from '../store/types';
import { DocumentPane } from './document-pane';
import FinalOutputs from './final-outputs';
import NewReportDialog from './report/new-report-dialog';
import ReportDocument from './report/report-document';
const OutputPane = () => {
  const $ = _c(25);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "agentBuilder.canvas.output"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  const {
    state,
    actions,
    isReadOnly
  } = useBuilder();
  const [isChoosingTask, setIsChoosingTask] = useState(false);
  const blankReportName = useBlankReportName();
  let t1;
  if ($[1] !== actions || $[2] !== blankReportName || $[3] !== isChoosingTask || $[4] !== isReadOnly || $[5] !== state.bundle.nodes || $[6] !== state.bundle.outputs?.artifactKey || $[7] !== t) {
    const tasks = state.bundle.nodes.filter(isTaskNode);
    const produced = tasks.flatMap(_temp);
    const artifactKey = state.bundle.outputs?.artifactKey;
    let t2;
    if ($[9] !== artifactKey) {
      t2 = t3 => {
        const {
          artifact: artifact_0
        } = t3;
        return artifact_0.id === artifactKey;
      };
      $[9] = artifactKey;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    const final = produced.find(t2);
    let t3;
    if ($[11] !== state.bundle.nodes) {
      t3 = task_0 => state.bundle.nodes.indexOf(task_0) + 1;
      $[11] = state.bundle.nodes;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    const position = t3;
    let t4;
    if ($[13] !== position || $[14] !== t) {
      t4 = task_1 => task_1.name || t(_temp2, {
        position: position(task_1)
      });
      $[13] = position;
      $[14] = t;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    const taskLabel = t4;
    const renderReport = () => {
      if (final) {
        return <ReportEditor key={final.artifact.id} task={final.task} taskLabel={taskLabel(final.task)} artifact={final.artifact} />;
      }
      if (artifactKey) {
        return <p className="text-body-3 text-tertiary">{t(_temp3)} {!isReadOnly && t(_temp4)}</p>;
      }
      if (isReadOnly) {
        return <p className="text-body-3 text-tertiary">{t(_temp5)}</p>;
      }
      if (tasks.length === 0) {
        return <p className="text-body-3 text-tertiary">{t(_temp6)}</p>;
      }
      if (produced.length > 0) {
        return null;
      }
      return <div className="flex flex-col items-center gap-3"><Card.Root><Card.Empty>{t(_temp7)}</Card.Empty></Card.Root><Button onClick={() => setIsChoosingTask(true)} prefixIcon={IcoPlusSmall16} size="compact" variant="secondary">{t(_temp8)}</Button><NewReportDialog defaultName={blankReportName} open={isChoosingTask} tasks={tasks.map(task_2 => ({
          id: task_2.id,
          label: taskLabel(task_2),
          position: position(task_2)
        }))} title={t(_temp9)} onClose={() => setIsChoosingTask(false)} onCreate={t5 => {
          const {
            taskId,
            ...report
          } = t5;
          if (taskId) {
            actions.startReport(taskId, report);
          }
        }} /></div>;
    };
    let t6;
    if ($[16] !== t) {
      t6 = t(_temp10);
      $[16] = t;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== t6) {
      t7 = <h2 className="text-heading-2 text-primary">{t6}</h2>;
      $[18] = t6;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== t) {
      t8 = t(_temp11);
      $[20] = t;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t10;
    let t11;
    let t9;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <ValidationAlerts nodeId={null} />;
      t10 = <FinalOutputs />;
      t11 = <OutputsSuggestions />;
      $[22] = t10;
      $[23] = t11;
      $[24] = t9;
    } else {
      t10 = $[22];
      t11 = $[23];
      t9 = $[24];
    }
    t1 = <DocumentPane id={OUTPUTS_NODE_ID} title={t7} subtitle={t8} contentClassName="gap-8">{t9}{t10}{t11}{renderReport()}</DocumentPane>;
    $[1] = actions;
    $[2] = blankReportName;
    $[3] = isChoosingTask;
    $[4] = isReadOnly;
    $[5] = state.bundle.nodes;
    $[6] = state.bundle.outputs?.artifactKey;
    $[7] = t;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
};
type ReportEditorProps = {
  task: Task;
  taskLabel: string;
  artifact: Artifact;
};
const ReportEditor = t0 => {
  const $ = _c(31);
  const {
    task,
    taskLabel,
    artifact
  } = t0;
  const {
    actions,
    isReadOnly
  } = useBuilder();
  const save = useNodeSave(task.id);
  const [, openTask] = useQueryState("node", parseAsString);
  let t1;
  if ($[0] !== task.id) {
    t1 = {
      nodeId: task.id
    };
    $[0] = task.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !isReadOnly;
  let t3;
  if ($[2] !== openTask || $[3] !== task.id) {
    t3 = () => openTask(task.id);
    $[2] = openTask;
    $[3] = task.id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3 || $[6] !== taskLabel) {
    t4 = <Trans ns="agents" i18nKey={_temp12} components={{
      link: <LinkButton className="!align-baseline underline! underline-offset-2" iconComponent={IcoArrowRightSmall16} onClick={t3} variant="body-2">{taskLabel}</LinkButton>
    }} />;
    $[5] = t3;
    $[6] = taskLabel;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  let t6;
  if ($[8] !== actions.tasks || $[9] !== artifact.id || $[10] !== task.id) {
    t5 = name => actions.tasks.updateArtifact(task.id, artifact.id, {
      name
    });
    t6 = (spec, options) => actions.tasks.updateArtifact(task.id, artifact.id, {
      spec
    }, options);
    $[8] = actions.tasks;
    $[9] = artifact.id;
    $[10] = task.id;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  if ($[13] !== artifact || $[14] !== task.id) {
    t7 = <ArtifactSuggestions taskId={task.id} artifact={artifact} />;
    $[13] = artifact;
    $[14] = task.id;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== artifact.name || $[17] !== artifact.spec || $[18] !== artifact.type || $[19] !== t2 || $[20] !== t4 || $[21] !== t5 || $[22] !== t6 || $[23] !== t7) {
    t8 = <ReportDocument name={artifact.name} spec={artifact.spec} type={artifact.type} editable={t2} byline={t4} onName={t5} onSpec={t6}>{t7}</ReportDocument>;
    $[16] = artifact.name;
    $[17] = artifact.spec;
    $[18] = artifact.type;
    $[19] = t2;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t1 || $[26] !== t8) {
    t9 = <TaskBlockProvider value={t1}>{t8}</TaskBlockProvider>;
    $[25] = t1;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== save || $[29] !== t9) {
    t10 = <section onBlur={save}>{t9}</section>;
    $[28] = save;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
};
export default OutputPane;
function _temp(task) {
  return task.artifacts.map(artifact => ({
    task,
    artifact
  }));
}
function _temp2($) {
  return $.unnamedTask;
}
function _temp3($_0) {
  return $_0.unresolved;
}
function _temp4($_1) {
  return $_1.unresolvedPick;
}
function _temp5($_2) {
  return $_2.noFinal;
}
function _temp6($_3) {
  return $_3.emptyNoTasks;
}
function _temp7($_4) {
  return $_4.startReportHint;
}
function _temp8($_5) {
  return $_5.startReport;
}
function _temp9($_6) {
  return $_6.startReport;
}
function _temp10($_7) {
  return $_7.title;
}
function _temp11($_8) {
  return $_8.description;
}
function _temp12($) {
  return $.agentBuilder.canvas.output.writtenBy;
}